import { inject } from '@angular/core';
import type { Route } from '@angular/router';

import { LanguageService } from '@rpgc-ai/services/translate/language.service';

import { EChatbotCreateStatus } from '@rpgc-ai/shared-interfaces/interfaces/chatbot-create.interface';

import { authGuard, tosGuard } from './core/guards/auth.guard';
import { DevelopEnvironmentGuard } from './core/guards/develop-environment.guard';
import { LanguageGuard } from './core/guards/language.guard';
import { seoResolver } from './core/resolvers/seo.resolver';
import { modalRoutes } from './modal.routes';

export const appRoutes: Route[] = [
  // Маршрут для отображения SVG по адресу /null
  {
    path: 'null',
    loadComponent: () => import('@rpgc-ai/pages/svg-page/svg-page.component').then((m) => m.SvgPageComponent),
  },
  ...modalRoutes,
  {
    path: ':lang',
    canActivate: [LanguageGuard],
    children: [
      /**
       * Chat
       */
      {
        path: 'chat/:id',
        loadComponent: () => import('@rpgc-ai/layout/chat/chat.component').then((m) => m.ChatComponent),
        resolve: { seo: seoResolver },
        canActivate: [tosGuard],
      },
      {
        path: '',
        loadComponent: () => import('@rpgc-ai/layout/main/main.component').then((m) => m.MainComponent),
        children: [
          /**
           * Chatbots catalog
           */
          {
            path: '',
            loadComponent: () => import('@rpgc-ai/pages/home/home.component').then((m) => m.HomeComponent),
            resolve: { seo: seoResolver },
            data: {
              seo: {
                titleKey: 'SEO.HOME_TITLE',
                descriptionKey: 'SEO.HOME_DESCRIPTION',
              },
            },
          },
          {
            path: 'catalog',
            loadComponent: () => import('@rpgc-ai/pages/catalog/catalog.component').then((m) => m.CatalogComponent),
            resolve: { seo: seoResolver },
            data: {
              seo: {
                titleKey: 'SEO.CATALOG_TITLE',
                descriptionKey: 'SEO.CATALOG_DESCRIPTION',
              },
            },
          },
          {
            path: 'my-profile',
            data: { title: 'My profile', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/my-profile/my-profile.component').then((m) => m.MyProfileComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard],
          },

          /**
           * Создание чатбота
           */
          {
            path: 'create-chatbot',
            data: { chatbotStatus: EChatbotCreateStatus.CREATE },
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Просмотр и редактирование чатбота
           */
          {
            path: 'view-chatbot/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'view-chatbot/:creatorSlug/:botId',
            loadComponent: () => import('@rpgc-ai/pages/chatbot/chatbot-create.component').then((m) => m.ChatbotCreateComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Мои чатботы
           */
          {
            path: 'my-chatbots',
            loadComponent: () => import('@rpgc-ai/pages/my-chatbots/my-chatbots.component').then((m) => m.MyChatbotsComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * Избранные чатботы
           */
          {
            path: 'favorites',
            loadComponent: () => import('@rpgc-ai/pages/favorites/favorites.component').then((m) => m.FavoritesComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          /**
           * История чатов
           */
          {
            path: 'chat-history',
            data: { title: 'Chat History', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/chat-history/chat-history.component').then((m) => m.ChatHistoryComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'blocked-creators',
            data: { title: 'Blocked Creators', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/blocked-creators/blocked-creators.component').then((m) => m.BlockedCreatorsComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'my-personas',
            data: { title: 'My Personas', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/my-personas/my-personas.component').then((m) => m.MyPersonasComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'create-persona',
            data: { title: 'Create Persona', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'edit-persona/:id',
            data: { title: 'Edit Persona', backLink: true },
            loadComponent: () =>
              import('@rpgc-ai/pages/create-persona-page/create-persona-page.component').then((m) => m.CreatePersonaPageComponent),
            resolve: { seo: seoResolver },
            canActivate: [authGuard, tosGuard],
          },
          {
            path: 'subscribe',
            data: {
              title: 'Subscribe',
              backLink: true,
              seo: {
                titleKey: 'SEO.SUBSCRIBE_TITLE',
                descriptionKey: 'SEO.SUBSCRIBE_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/subscribe-page/subscribe-page.component').then((m) => m.SubscribePageComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'subscribe/:section',
            data: { title: 'Subscribe', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/subscribe-page/subscribe-page.component').then((m) => m.SubscribePageComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'help',
            data: {
              title: 'Help',
              seo: {
                titleKey: 'SEO.HELP_TITLE',
                descriptionKey: 'SEO.HELP_DESCRIPTION',
              },
            },
            loadChildren: () => import('@rpgc-ai/pages/help/help.routes').then((r) => r.helpRoutes),
            resolve: { seo: seoResolver },
          },
          {
            path: 'profile/:id',
            data: { title: 'User profile', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/public-profile/public-profile.component').then((m) => m.PublicProfileComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'need-auth',
            loadComponent: () =>
              import('@rpgc-ai/lazy-blocks/do-you-need-auth/do-you-need-auth.component').then((m) => m.DoYouNeedAuthComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'terms-and-conditions',
            data: {
              title: 'Terms & Conditions',
              backLink: true,
              seo: {
                titleKey: 'SEO.TERMS_TITLE',
                descriptionKey: 'SEO.TERMS_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/terms-and-conditions/terms-and-conditions.component').then((m) => m.TermsAndConditionsComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'privacy-policy',
            data: {
              title: 'Privacy Policy',
              backLink: true,
              seo: {
                titleKey: 'SEO.PRIVACY_TITLE',
                descriptionKey: 'SEO.PRIVACY_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/privacy-policy/privacy-policy.component').then((m) => m.PrivacyPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'refund-policy',
            data: {
              title: 'Privacy Policy',
              backLink: true,
              seo: {
                titleKey: 'SEO.REFUND_TITLE',
                descriptionKey: 'SEO.REFUND_DESCRIPTION',
              },
            },
            resolve: { seo: seoResolver },
            loadComponent: () => import('@rpgc-ai/pages/refund-policy/refund-policy.component').then((m) => m.RefundPolicyComponent),
          },
          {
            path: 'languages-admin',
            data: { title: 'Languages Admin', backLink: true },
            loadComponent: () => import('@rpgc-ai/pages/languages-admin/languages-admin.component').then((m) => m.LanguagesAdminComponent),
            resolve: { seo: seoResolver },
            canActivate: [DevelopEnvironmentGuard],
          },
          {
            path: 'blocked-content-policy',
            data: {
              seo: {
                titleKey: 'SEO.BLOCKED_CONTENT_TITLE',
                descriptionKey: 'SEO.BLOCKED_CONTENT_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/blocked-content-policy/blocked-content-policy.component').then((m) => m.BlockedContentPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'community-guidelines',
            data: {
              seo: {
                titleKey: 'SEO.COMMUNITY_TITLE',
                descriptionKey: 'SEO.COMMUNITY_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/community-guidelines/community-guidelines.component').then((m) => m.CommunityGuidelinesComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'content-removal-policy',
            data: {
              seo: {
                titleKey: 'SEO.CONTENT_REMOVAL_TITLE',
                descriptionKey: 'SEO.CONTENT_REMOVAL_DESCRIPTION',
              },
            },
            loadComponent: () =>
              import('@rpgc-ai/pages/content-removal-policy/content-removal-policy.component').then((m) => m.ContentRemovalPolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'underage-policy',
            data: {
              seo: {
                titleKey: 'SEO.UNDERAGE_TITLE',
                descriptionKey: 'SEO.UNDERAGE_DESCRIPTION',
              },
            },
            loadComponent: () => import('@rpgc-ai/pages/underage-policy/underage-policy.component').then((m) => m.UnderagePolicyComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: 'payment/:status/:id',
            loadComponent: () => import('@rpgc-ai/pages/payment/payment.component').then((m) => m.PaymentComponent),
            resolve: { seo: seoResolver },
          },
          {
            path: '**',
            redirectTo: '',
          },
        ],
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  // Wildcard маршрут для обработки всех несуществующих URL
  {
    path: '',
    pathMatch: 'full',
    redirectTo: () => {
      const languageService = inject(LanguageService);
      return languageService.initLanguage();
    },
  },
  {
    path: '**',
    redirectTo: () => {
      const languageService = inject(LanguageService);
      return languageService.initLanguage();
    },
  },
];
